import React, { useState, useEffect } from 'react';
import api from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Play, Pause, RefreshCw, Settings } from 'lucide-react';

const RecurringExpenses = () => {
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [insertDialogOpen, setInsertDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToInsert, setItemToInsert] = useState(null);
  const [insertAmount, setInsertAmount] = useState('');
  const [typeFormData, setTypeFormData] = useState({ name: '' });
  const [formData, setFormData] = useState({
    type_id: '',
    frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    amount: '',
    category_id: '',
    payment_method_id: '',
    is_fixed_amount: true,
    auto_insert: false,
    notes: '',
  });

  const frequencyOptions = [
    { value: 'monthly', label: 'Mensile' },
    { value: 'bimonthly', label: 'Bimestrale' },
    { value: 'quarterly', label: 'Trimestrale' },
    { value: 'semiannual', label: 'Semestrale' },
    { value: 'annual', label: 'Annuale' },
  ];

  const getFrequencyLabel = (value) => {
    const option = frequencyOptions.find(o => o.value === value);
    return option ? option.label : value;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expensesRes, typesRes, categoriesRes, paymentMethodsRes] = await Promise.all([
        api.get('/recurring-expenses'),
        api.get('/recurring-expense-types'),
        api.get('/categories'),
        api.get('/payment-methods'),
      ]);
      setRecurringExpenses(expensesRes.data);
      setExpenseTypes(typesRes.data);
      setCategories(categoriesRes.data.sort((a, b) => a.name.localeCompare(b.name)));
      setPaymentMethods(paymentMethodsRes.data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const getTypeName = (typeId) => {
    const type = expenseTypes.find(t => t.id === typeId);
    return type ? type.name : 'N/A';
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'N/A';
  };

  const getCategoryColor = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.color : '#999999';
  };

  const getPaymentMethodName = (paymentMethodId) => {
    const method = paymentMethods.find(m => m.id === paymentMethodId);
    return method ? method.name : 'N/A';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Type CRUD
  const handleOpenTypeDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setTypeFormData({ name: type.name });
    } else {
      setEditingType(null);
      setTypeFormData({ name: '' });
    }
    setTypeDialogOpen(true);
  };

  const handleSubmitType = async (e) => {
    e.preventDefault();
    try {
      if (editingType) {
        await api.put(`/recurring-expense-types/${editingType.id}`, typeFormData);
        toast.success('Tipologia aggiornata');
      } else {
        await api.post('/recurring-expense-types', typeFormData);
        toast.success('Tipologia creata');
      }
      setTypeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleDeleteType = async (type) => {
    try {
      await api.delete(`/recurring-expense-types/${type.id}`);
      toast.success('Tipologia eliminata');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  // Expense CRUD
  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setEditingItem(expense);
      setFormData({
        type_id: expense.type_id,
        frequency: expense.frequency,
        start_date: expense.start_date.split('T')[0],
        amount: expense.amount.toString(),
        category_id: expense.category_id || '',
        payment_method_id: expense.payment_method_id || '',
        is_fixed_amount: expense.is_fixed_amount,
        auto_insert: expense.auto_insert,
        notes: expense.notes || '',
      });
    } else {
      setEditingItem(null);
      setFormData({
        type_id: expenseTypes.length > 0 ? expenseTypes[0].id : '',
        frequency: 'monthly',
        start_date: new Date().toISOString().split('T')[0],
        amount: '',
        category_id: categories.length > 0 ? categories[0].id : '',
        payment_method_id: paymentMethods.length > 0 ? paymentMethods[0].id : '',
        is_fixed_amount: true,
        auto_insert: false,
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.type_id) {
      toast.error('Seleziona una tipologia');
      return;
    }

    if (!formData.category_id) {
      toast.error('Seleziona una categoria');
      return;
    }

    if (!formData.payment_method_id) {
      toast.error('Seleziona un metodo di pagamento');
      return;
    }
    
    const payload = {
      ...formData,
      amount: parseFloat(formData.amount),
      start_date: new Date(formData.start_date).toISOString(),
    };

    try {
      if (editingItem) {
        await api.put(`/recurring-expenses/${editingItem.id}`, payload);
        toast.success('Spesa ricorrente aggiornata');
      } else {
        await api.post('/recurring-expenses', payload);
        toast.success('Spesa ricorrente creata');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/recurring-expenses/${itemToDelete.id}`);
      toast.success('Spesa ricorrente eliminata');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleToggleActive = async (expense) => {
    try {
      await api.put(`/recurring-expenses/${expense.id}`, {
        is_active: !expense.is_active,
      });
      toast.success(expense.is_active ? 'Spesa sospesa' : 'Spesa riattivata');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleOpenInsertDialog = (expense) => {
    setItemToInsert(expense);
    setInsertAmount(expense.amount.toString());
    setInsertDialogOpen(true);
  };

  const handleManualInsert = async () => {
    try {
      const amount = parseFloat(insertAmount);
      await api.post(`/recurring-expenses/${itemToInsert.id}/insert?amount=${amount}`);
      toast.success('Spesa inserita nel registro');
      setInsertDialogOpen(false);
      setItemToInsert(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'inserimento');
    }
  };

  const handleProcessAll = async () => {
    try {
      const response = await api.post('/recurring-expenses/process');
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'elaborazione');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-600">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recurring-expenses-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            Spese Ricorrenti
          </h2>
          <p className="text-slate-600">Gestisci le tue spese fisse e programmate</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleProcessAll}
            variant="outline"
            className="border-[#D94E1F] text-[#D94E1F] hover:bg-[#D94E1F]/10"
            data-testid="process-all-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Elabora Automatiche
          </Button>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-full"
            data-testid="add-recurring-expense-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuova Spesa
          </Button>
        </div>
      </div>

      <Tabs defaultValue="expenses" className="w-full">
        <TabsList className="inline-flex min-w-max w-full sm:w-auto bg-slate-100 border border-slate-200 h-auto p-1.5 gap-1 mb-6">
          <TabsTrigger 
            value="expenses" 
            className="px-4 py-2 text-sm font-medium bg-white text-slate-700 data-[state=active]:!bg-[#D94E1F] data-[state=active]:!text-white rounded-md"
          >
            Spese Ricorrenti
          </TabsTrigger>
          <TabsTrigger 
            value="types" 
            className="px-4 py-2 text-sm font-medium bg-white text-slate-700 data-[state=active]:!bg-[#D94E1F] data-[state=active]:!text-white rounded-md"
          >
            <Settings className="w-4 h-4 mr-2" />
            Tipologie
          </TabsTrigger>
        </TabsList>

        <TabsContent value="expenses" className="space-y-4">
          {recurringExpenses.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <RefreshCw className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Nessuna spesa ricorrente configurata</p>
              <p className="text-sm mt-2">Aggiungi la tua prima spesa ricorrente</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {recurringExpenses.map((expense) => (
                <div
                  key={expense.id}
                  className={`p-4 rounded-lg border ${
                    expense.is_active 
                      ? 'bg-white border-slate-200' 
                      : 'bg-slate-50 border-slate-200 opacity-60'
                  }`}
                  data-testid={`recurring-expense-${expense.id}`}
                >
                  <div className="flex flex-col sm:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-slate-900">
                          {getTypeName(expense.type_id)}
                        </h3>
                        {!expense.is_active && (
                          <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                            Sospesa
                          </span>
                        )}
                        {expense.auto_insert && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                            Automatica
                          </span>
                        )}
                        {!expense.is_fixed_amount && (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                            Importo variabile
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-sm text-slate-600">
                        <div>
                          <span className="text-slate-400">Importo:</span>{' '}
                          <span className="font-medium text-slate-900">{formatAmount(expense.amount)}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Categoria:</span>{' '}
                          <span className="inline-flex items-center gap-1">
                            <span 
                              className="w-3 h-3 rounded-full inline-block"
                              style={{ backgroundColor: getCategoryColor(expense.category_id) }}
                            />
                            {getCategoryName(expense.category_id)}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400">Pagamento:</span>{' '}
                          {getPaymentMethodName(expense.payment_method_id)}
                        </div>
                        <div>
                          <span className="text-slate-400">Frequenza:</span>{' '}
                          {getFrequencyLabel(expense.frequency)}
                        </div>
                        <div>
                          <span className="text-slate-400">Prossima:</span>{' '}
                          {formatDate(expense.next_due_date)}
                        </div>
                        <div>
                          <span className="text-slate-400">Inizio:</span>{' '}
                          {formatDate(expense.start_date)}
                        </div>
                      </div>
                      {expense.notes && (
                        <p className="text-sm text-slate-500 mt-2">{expense.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenInsertDialog(expense)}
                        className="text-green-600 border-green-200 hover:bg-green-50"
                        title="Inserisci nel registro"
                        data-testid={`insert-${expense.id}`}
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(expense)}
                        className={expense.is_active ? 'text-yellow-600' : 'text-green-600'}
                        title={expense.is_active ? 'Sospendi' : 'Riattiva'}
                        data-testid={`toggle-${expense.id}`}
                      >
                        {expense.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(expense)}
                        className="text-slate-600 hover:text-[#D94E1F]"
                        data-testid={`edit-${expense.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setItemToDelete(expense);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-slate-600 hover:text-red-600"
                        data-testid={`delete-${expense.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="types" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => handleOpenTypeDialog()}
              className="bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-full"
              data-testid="add-type-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuova Tipologia
            </Button>
          </div>
          
          {expenseTypes.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Settings className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Nessuna tipologia configurata</p>
              <p className="text-sm mt-2">Crea tipologie come "Affitto", "Bollette", "Abbonamenti"</p>
            </div>
          ) : (
            <div className="space-y-3">
              {expenseTypes.map((type) => (
                <div
                  key={type.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-100 bg-white"
                  data-testid={`type-${type.id}`}
                >
                  <span className="font-medium text-slate-900">{type.name}</span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenTypeDialog(type)}
                      className="text-slate-600 hover:text-[#D94E1F]"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteType(type)}
                      className="text-slate-600 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Expense Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Modifica Spesa Ricorrente' : 'Nuova Spesa Ricorrente'}
            </DialogTitle>
            <DialogDescription>
              {editingItem ? 'Modifica i dettagli della spesa' : 'Configura una nuova spesa ricorrente'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="type_id">Tipologia *</Label>
                <Select
                  value={formData.type_id}
                  onValueChange={(value) => setFormData({ ...formData, type_id: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="type-select">
                    <SelectValue placeholder="Seleziona tipologia" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {expenseTypes.length === 0 && (
                  <p className="text-sm text-yellow-600 mt-1">
                    Prima crea almeno una tipologia nel tab "Tipologie"
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="frequency">Frequenza *</Label>
                  <Select
                    value={formData.frequency}
                    onValueChange={(value) => setFormData({ ...formData, frequency: value })}
                  >
                    <SelectTrigger className="mt-2" data-testid="frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {frequencyOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="start_date">Data di Partenza *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="mt-2"
                    required
                    data-testid="start-date-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="category_id">Categoria *</Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="category-select">
                    <SelectValue placeholder="Seleziona categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <span 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: cat.color }}
                          />
                          {cat.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {categories.length === 0 && (
                  <p className="text-sm text-yellow-600 mt-1">
                    Prima crea almeno una categoria nelle Impostazioni
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="payment_method_id">Metodo di Pagamento *</Label>
                <Select
                  value={formData.payment_method_id}
                  onValueChange={(value) => setFormData({ ...formData, payment_method_id: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="payment-method-select">
                    <SelectValue placeholder="Seleziona metodo di pagamento" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map((method) => (
                      <SelectItem key={method.id} value={method.id}>
                        {method.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {paymentMethods.length === 0 && (
                  <p className="text-sm text-yellow-600 mt-1">
                    Prima crea almeno un metodo di pagamento nelle Impostazioni
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="amount">Importo (€) *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="mt-2"
                  required
                  placeholder="0.00"
                  data-testid="amount-input"
                />
              </div>

              <div className="space-y-3 pt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_fixed_amount"
                    checked={formData.is_fixed_amount}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, is_fixed_amount: checked })
                    }
                    data-testid="fixed-amount-checkbox"
                  />
                  <Label htmlFor="is_fixed_amount" className="text-sm font-normal cursor-pointer">
                    Importo fisso (l'importo rimane sempre lo stesso)
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="auto_insert"
                    checked={formData.auto_insert}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, auto_insert: checked })
                    }
                    data-testid="auto-insert-checkbox"
                  />
                  <Label htmlFor="auto_insert" className="text-sm font-normal cursor-pointer">
                    Inserisci automaticamente (la spesa viene aggiunta automaticamente alla scadenza)
                  </Label>
                </div>
              </div>

              <div>
                <Label htmlFor="notes">Note</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="mt-2"
                  placeholder="Note opzionali..."
                  data-testid="notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button
                type="submit"
                className="bg-[#D94E1F] hover:bg-[#B83E15] text-white"
                disabled={!formData.type_id || !formData.category_id || !formData.payment_method_id || expenseTypes.length === 0 || categories.length === 0 || paymentMethods.length === 0}
                data-testid="save-expense-btn"
              >
                {editingItem ? 'Salva' : 'Crea'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Type Dialog */}
      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent className="sm:max-w-[400px]" data-testid="type-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingType ? 'Modifica Tipologia' : 'Nuova Tipologia'}
            </DialogTitle>
            <DialogDescription>
              {editingType ? 'Modifica il nome della tipologia' : 'Crea una nuova tipologia di spesa ricorrente'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitType}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="type_name">Nome *</Label>
                <Input
                  id="type_name"
                  value={typeFormData.name}
                  onChange={(e) => setTypeFormData({ name: e.target.value })}
                  className="mt-2"
                  required
                  placeholder="Es. Affitto, Bollette, Netflix..."
                  data-testid="type-name-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTypeDialogOpen(false)}>
                Annulla
              </Button>
              <Button
                type="submit"
                className="bg-[#D94E1F] hover:bg-[#B83E15] text-white"
                data-testid="save-type-btn"
              >
                {editingType ? 'Salva' : 'Crea'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sei sicuro?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione non può essere annullata. La spesa ricorrente verrà eliminata definitivamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manual Insert Dialog */}
      <Dialog open={insertDialogOpen} onOpenChange={setInsertDialogOpen}>
        <DialogContent className="sm:max-w-[400px]" data-testid="insert-dialog">
          <DialogHeader>
            <DialogTitle>Inserisci Spesa</DialogTitle>
            <DialogDescription>
              Inserisci questa spesa ricorrente nel registro spese
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="insert_amount">Importo (€)</Label>
              <Input
                id="insert_amount"
                type="number"
                step="0.01"
                min="0"
                value={insertAmount}
                onChange={(e) => setInsertAmount(e.target.value)}
                className="mt-2"
                data-testid="insert-amount-input"
              />
              {itemToInsert && !itemToInsert.is_fixed_amount && (
                <p className="text-sm text-slate-500 mt-1">
                  Puoi modificare l'importo per questa occorrenza
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setInsertDialogOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleManualInsert}
              className="bg-[#D94E1F] hover:bg-[#B83E15] text-white"
              data-testid="confirm-insert-btn"
            >
              Inserisci
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecurringExpenses;
